import { ClerkMetadataKey } from '@this+that/utils';
import { sanitizeHtml } from '@this+that/utils/sanitizeHtml';

import {
  escapeHtmlText,
  hasTemplateContent,
  isContentEmpty,
  splitEditorContent,
} from './editorContentUtils';
import {
  extractSignatureBlock,
  hasSignatureBlock,
  stripSignatureBlock,
} from './emailSignatureBlock';
import {
  ATTRIBUTE_VALUES,
  DATA_ATTRIBUTES,
} from '../hooks/richTextArea/constants';

export { extractSignatureBlock, stripSignatureBlock };

export const EMAIL_SIGNATURE_MAX_HTML_LENGTH = 2000;

const EMPTY_PARAGRAPH = '<p></p>';
const USER_INPUT_CLOSING_TAG = '</div>';

export interface EmailSignatureConfig {
  enabled: boolean;
  html: string;
}

export const DISABLED_EMAIL_SIGNATURE: EmailSignatureConfig = {
  enabled: false,
  html: '',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const readStoredEmailSignature = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): EmailSignatureConfig | null => {
  const stored = unsafeMetadata?.[ClerkMetadataKey.EMAIL_SIGNATURE];
  if (
    isRecord(stored) &&
    typeof stored.enabled === 'boolean' &&
    typeof stored.html === 'string'
  ) {
    return { enabled: stored.enabled, html: stored.html };
  }
  return null;
};

export const resolveEmailSignatureConfig = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): EmailSignatureConfig =>
  readStoredEmailSignature(unsafeMetadata) ?? DISABLED_EMAIL_SIGNATURE;

export const isEmailSignatureOverLimit = (html: string): boolean =>
  html.length > EMAIL_SIGNATURE_MAX_HTML_LENGTH;

export const buildDefaultSignatureHtml = (
  fullName: string | null | undefined,
  title: string | null | undefined,
): string => {
  const name = fullName?.trim();
  const role = title?.trim();
  const nameLine = name
    ? `<p><strong>${escapeHtmlText(name)}</strong></p>`
    : '';
  const roleLine = role ? `<p>${escapeHtmlText(role)}</p>` : '';
  return `${nameLine}${roleLine}`;
};

export const readUserTitle = (
  unsafeMetadata: Record<string, unknown> | null | undefined,
): string | undefined => {
  const title = unsafeMetadata?.[ClerkMetadataKey.USER_TITLE];
  return typeof title === 'string' ? title : undefined;
};

export const buildSignatureBlock = (html: string): string => {
  const sanitized = sanitizeHtml(html);
  if (!sanitized) {
    return '';
  }

  const doc = new DOMParser().parseFromString(sanitized, 'text/html');
  doc.querySelectorAll('img').forEach((image) => image.remove());
  if (!doc.body.textContent?.trim()) {
    return '';
  }

  const block = doc.createElement('div');
  block.setAttribute(DATA_ATTRIBUTES.EMAIL_SIGNATURE, ATTRIBUTE_VALUES.TRUE);
  block.append(...Array.from(doc.body.childNodes));
  return block.outerHTML;
};

const insertAtEndOfUserInput = (
  userInput: string,
  addition: string,
): string => {
  if (!userInput.includes(DATA_ATTRIBUTES.USER_INPUT_AREA)) {
    return `${userInput}${addition}`;
  }

  const closingIndex = userInput.lastIndexOf(USER_INPUT_CLOSING_TAG);
  if (closingIndex === -1) {
    return `${userInput}${addition}`;
  }
  return `${userInput.slice(0, closingIndex)}${addition}${userInput.slice(closingIndex)}`;
};

export const applySignatureToBody = (
  body: string,
  signatureBlock: string,
): string => {
  if (!signatureBlock) {
    return body;
  }

  const { userInput, quotedContent } = splitEditorContent(body);
  if (hasSignatureBlock(userInput)) {
    return body;
  }

  if (!hasTemplateContent(body)) {
    const base = isContentEmpty(body) ? EMPTY_PARAGRAPH : body;
    return `${base}${signatureBlock}`;
  }

  const withSignature = insertAtEndOfUserInput(
    userInput,
    `${EMPTY_PARAGRAPH}${signatureBlock}`,
  );
  return `${withSignature}${quotedContent}`;
};
